<template>
  <div
    class="i-promo-bundle-cta"
    role="region"
    aria-label="Klarna coaching bundle promo"
  >
    <div class="pill">LIMITED TIME OFFER</div>
    <div class="left-pane">
      <div class="headline">
        Exclusive {{ GlobalFunctions.setToLastDateTimeOfMonth().toLocaleString("default", { month: "long" }) }} Offer
      </div>
      <div class="subhead">Buy Now, Pay Later with</div>
      <div class="emblem">
        <img
          :src="iconJobtest"
          class="icon jobtest"
          alt="Jobtest logo"
        />
        <div class="divider" />
        <img
          :src="logoKlarna"
          class="icon klarna"
          alt="Klarna logo"
        />
      </div>
      <div class="subtext">Exclusive Partnership</div>
    </div>
    <div class="right-pane">
      <div class="top-row">
        <div class="headline">A free month of coaching, on us!</div>
        <div>Buy 3 months of coaching and <strong>get the 4th month free.</strong></div>
      </div>
      <div class="subhead">
        <div>
          <strong>With our Klarna coaching bundle you...</strong>
        </div>
        <ul>
          <li>Pay over time like your current subscription.</li>
          <li>Get a full month of coaching free!...<strong>That's it.</strong></li>
        </ul>
        <div class="pills">
          <span class="pill">✓ 0% Interest Payment</span>
          <span class="pill">✓ No Credit Check</span>
          <span class="pill">✓ No Fees</span>
        </div>
      </div>

      <iBaseButton
        v-bind="MAIN_BUTTON_STYLE"
        text="Claim Your Free Month  →"
        size="medium"
        font-size="14px"
        @clicked="goToCoachingBundle"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRouter } from "vue-router"

import { GlobalVariables } from "@/helpers/globalVariables"
import { GlobalFunctions } from "@/helpers/globalFunctions"

// COMPONENTS
import iBaseButton from "@/components/base/BaseButton.vue"

// ICONS
import logoKlarna from "@/assets/logos/logo-klarna.webp"
import iconJobtest from "@/assets/icons/icon-jobtest.svg"

// STYLES
import { bluePurpleGradient, white } from "@/styles/colors"

// ROUTER
const router = useRouter()

// CONSTANTS
const MAIN_BUTTON_STYLE = {
  backgroundImage: bluePurpleGradient,
  borderRadius: "12px",
  fontSize: "14px",
  fontFamily: "Arial",
  textColor: white,
  padding: "9px 32px",
  size: "small"
}

const goToCoachingBundle = () => {
  router.push({
    name: GlobalVariables.urls.coaching.children.coachingBundle.routeName
  })
}
</script>

<style scoped lang="scss">
.i-promo-bundle-cta {
  --klarna-brand-backgroundcolor: #ffa8cd;

  @include flex(column, center, stretch);

  position: relative;
  gap: 0;
  border-radius: 14px;
  border: 2px solid #be4fff;
  background: white;
  box-shadow:
    0 10px 15px -3px rgb(0 0 0 / 10%),
    0 4px 6px -4px rgb(0 0 0 / 10%);
  font-family: Inter, sans-serif;

  @media (min-width: $breakpoint-md) {
    @include flex(row, stretch, stretch);
  }

  > .pill {
    @include text-xxs;

    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    margin-left: auto;
    margin-right: auto;
    border-radius: 0 0 12px 12px;
    padding: 8px 16px;
    background: $blue-purple-gradient;
    color: white;
    font-weight: 600;

    @media (min-width: $breakpoint-md) {
      @include text-xs;

      left: unset;
      right: 0;
      transform: translateX(0);
      border-radius: 0 10px;
    }
  }

  .left-pane {
    @include flex(column, center, center);

    width: 100%;
    background: linear-gradient(135deg, rgb(190 79 255 / 20%) 0%, rgb(74 157 255 / 20%) 100%);
    gap: 0;
    padding: 48px 32px 32px;

    @media (min-width: $breakpoint-md) {
      padding: 32px;
      width: 312px;
    }

    .emblem {
      @include flex(row, center, center);

      .divider {
        width: 2px;
        height: 42px;
        background: $black900;
        margin: 14px;
      }

      .icon {
        width: 42px;
        height: 42px;

        &.jobtest {
          background-color: white;
          padding: 10px;
          border-radius: 50%;
          min-width: 42px;
        }

        &.klarna {
          width: calc(261.39px * 42 / 128.91);
          aspect-ratio: 261.39/128.91;
        }
      }
    }

    .headline {
      @include display-xs;

      font-weight: 700;
      text-align: center;
      display: inline-block;
      background: $blue-purple-gradient;
      background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .subhead {
      margin-top: 24px;
    }

    .subtext {
      @include text-xs;
    }
  }

  .right-pane {
    @include flex(column, center, center);

    padding: 16px 24px;
    gap: 24px;
    flex: 1 1 auto;

    @media (min-width: $breakpoint-md) {
      @include flex(column, flex-start, stretch);

      gap: 24px;
      padding: 32px;
    }

    .top-row {
      @include flex(column, space-between, center);

      text-align: center;

      @media (min-width: $breakpoint-md) {
        @include flex(column, space-between, stretch);

        text-align: left;
      }

      .headline {
        @include display-xs;

        font-weight: 700;
      }
    }

    .pills {
      @include flex(row, center, center, wrap);

      gap: 12px;
      margin-top: 24px;

      @media (min-width: $breakpoint-md) {
        @include flex(row, start, start, wrap);

        gap: 12px;
      }

      .pill {
        @include text-xs;

        font-weight: 700;
        color: $black700;
        background: $amethyst50;
        border-radius: 999px;
        border: solid 1px #e5e7eb;
        padding: 4px 12px;
      }
    }

    .subhead {
      @include text-sm;

      color: $black700;
      text-align: center;

      @media (min-width: $breakpoint-md) {
        text-align: left;
      }

      ul {
        text-align: left;
        padding-left: 16px;

        li + li {
          padding-top: 8px;
        }
      }
    }
  }
}
</style>
